import { Button, Card, DatePicker, Form, Grid, Input, Message, Select, Space } from "@arco-design/web-react";
import { useEffect, useCallback, useState } from "react";
import { CompanyAPI } from "@/apis/system";
import { ItemReceiptAPI, POAPI } from "@/apis/purchasing";
import { CustomerAPI } from "@/apis/sales";
import { InputNumber } from "@/components/Numeric";
import { EnumSelect } from "@/components/Enum";
import { ReferenceTypeDef } from "@/enums/purchasing";
import { IconDelete, IconPlus } from "@arco-design/web-react/icon";
import Address from "@/components/Address";
import WarehouseSelect from "@/components/WarehouseSelect";
import { Link, useNavigate, useParams } from "react-router-dom";
import { today } from "@/utils";
import _ from "lodash";

const FormItem = Form.Item;
const Row = Grid.Row;
const Col = Grid.Col;
const TextArea = Input.TextArea;
const LocationSelect = WarehouseSelect.LocationSelect;

export const ReceiptAdd = () => {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const params = useParams();
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState([]);
    const [shipAddresses, setShipAddresses] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [dropShip, setDropShip] = useState(null);
    const [warehouseId, setWarehouseId] = useState(null);
    const [poNo, setPoNo] = useState();

    useEffect(() => {
        const fetchCustomers = async () => {
            const values = await CustomerAPI.actives();
            setCustomers(values);
        };
        fetchCustomers();
    }, []);

    const getAddresses = useCallback(async (dropShip) => {
        if (dropShip) {
            const customer = await CustomerAPI.get(dropShip);
            setShipAddresses(customer.shipAddresses);
        } else {
            const companyShipToList = await CompanyAPI.addresses();
            setShipAddresses(companyShipToList);
        }
    }, []);

    useEffect(() => {
        const init = async () => {
            const values = await POAPI.get(params.poId);
            const receiveds = await ItemReceiptAPI.received(params.poId);
            _.forEach(values.items, (item) => {
                const receivedQuantity = receiveds[item.itemId];
                if (receivedQuantity) {
                    item.quantity = Math.max(0, item.quantity - receivedQuantity);
                    item.amount = item.quantity * item.rate;
                }
            });
            values.items = _.filter(values.items, item => item.quantity > 0);

            setPoNo(values.poNo);
            setDropShip(values.dropShip);

            const formData = {
                vendorId: values.vendorId,
                vendorName: values.vendorName,
                dropShip: values.dropShip,
                shipToId: values.shipToId,
                shipToContactName: values.shipToContactName,
                shipToAddress: values.shipToAddress,
                shipToCity: values.shipToCity,
                shipToState: values.shipToState,
                shipToZip: values.shipToZip,
                poId: values.id,
                poNo: values.poNo,
                reference: values.reference,
                items: _.map(values.items, item => ({
                    itemId: item.itemId,
                    itemName: item.itemName,
                    itemNo: item.itemNo,
                    description: item.description,
                    uom: item.uom,
                    quantity: item.quantity,
                    rate: item.rate,
                    amount: item.amount
                })),
                receiveDate: today()
            };

            setItems(values.items);
            form.setFieldsValue(formData);
        };

        init();
    }, [form, params]);

    useEffect(() => {
        getAddresses(dropShip);
    }, [getAddresses, dropShip]);

    const calcAmount = (index) => {
        const rate = form.getFieldValue("items[" + index + "].rate");
        const quantity = form.getFieldValue("items[" + index + "].quantity");
        if (rate && quantity) {
            const amount = rate * quantity;
            form.setFieldValue("items[" + index + "].amount", amount);
        }
    }

    const onSubmit = async () => {
        try {
            const values = await form.validate();
            setLoading(true);
            const receiptId = await ItemReceiptAPI.add(values);
            Message.success("Receipt created successfully");
            navigate(`/workspace/purchasing/receipt/${receiptId}`);
        } catch (error) {
            if (error.name !== 'ValidationError') {
                Message.error(error.message || "Failed to create Receipt");
            }
        } finally {
            setLoading(false);
        }
    }

    return (
        <Card title={`Item Receipt [${poNo}]`} extra={
            <Space>
                <Button type="primary" onClick={onSubmit} loading={loading}>Save</Button>
                <Link to={`/workspace/purchasing/po/${params.poId}`}>
                    <Button>Cancel</Button>
                </Link>
            </Space>
        }>
            <Form form={form} colon
                labelCol={{ span: 0 }}
                wrapperCol={{ span: 24 }}
            >
                <FormItem field="id" hidden>
                    <Input />
                </FormItem>
                <FormItem field="poId" hidden>
                    <Input />
                </FormItem>
                <FormItem>
                    <Row gutter={8}>
                        <Col span={8}>
                            <FormItem label="Vendor" field="vendorName" labelCol={{ span: 9 }}>
                                <Input readOnly />
                            </FormItem>
                        </Col>
                        <Col span={8}>
                            <FormItem label="Drop Ship" field="dropShip" labelCol={{ span: 8 }} wrapperCol={{ span: 16 }}>
                                <Select options={_.map(customers, (item) => ({ value: item.id, label: item.customerName }))}
                                    onChange={val => {
                                        setDropShip(val);
                                        form.clearFields("shipToContactName");
                                    }} />
                            </FormItem>
                            <FormItem label="Ship To" field="shipToContactName" labelCol={{ span: 8 }} wrapperCol={{ span: 16 }}
                                required rules={[{ required: true }]}>
                                <Address.Select addresses={shipAddresses} onAddrChange={val => {
                                    form.setFieldValue("shipToAddress", val.address);
                                    form.setFieldValue("shipToCity", val.city);
                                    form.setFieldValue("shipToState", val.state);
                                    form.setFieldValue("shipToZip", val.zip);
                                }} />
                            </FormItem>
                            <FormItem field="shipToAddress" hidden>
                                <Input />
                            </FormItem>
                            <FormItem field="shipToCity" hidden>
                                <Input />
                            </FormItem>
                            <FormItem field="shipToState" hidden>
                                <Input />
                            </FormItem>
                            <FormItem field="shipToZip" hidden>
                                <Input />
                            </FormItem>
                            <FormItem shouldUpdate noStyle>
                                {(value) => {
                                    return (
                                        <FormItem wrapperCol={{ offset: 8 }}>
                                            <Address value={{
                                                name: value.shipToContactName,
                                                address: value.shipToAddress,
                                                city: value.shipToCity,
                                                state: value.shipToState,
                                                zip: value.shipToZip,
                                            }} />
                                        </FormItem>

                                    )
                                }}
                            </FormItem>
                        </Col>
                        <Col span={8}>
                            <FormItem label="From PO" field="poNo" labelCol={{ span: 8 }} >
                                <Input readOnly />
                            </FormItem>
                            <FormItem label="Receive Date" field="receiveDate" labelCol={{ span: 8 }} required rules={[{ required: true }]}>
                                <DatePicker />
                            </FormItem>
                        </Col>
                    </Row>
                    <Row>
                        <Col span={24}>
                            <FormItem label="Reference" labelCol={{ span: 3 }}>
                                <Form.List field="reference">
                                    {(fields, { add, remove }) =>
                                        <>
                                            {fields.map((item, index) => {
                                                return (
                                                    <FormItem key={item.key} style={{ marginBottom: 0 }}>
                                                        <Space className="form-item-row">
                                                            <FormItem field={item.field + ".type"}>
                                                                <EnumSelect enumObj={ReferenceTypeDef} order="label" style={{ width: 200 }} showSearch />
                                                            </FormItem>
                                                            <FormItem field={item.field + ".number"}>
                                                                <Input />
                                                            </FormItem>
                                                            <Button type="text" status="danger" icon={<IconDelete />} onClick={() => remove(index)}></Button>
                                                        </Space>
                                                    </FormItem>
                                                )
                                            })}
                                            <Button type="text" icon={<IconPlus />} onClick={() => add()}>Add Reference</Button>
                                        </>
                                    }
                                </Form.List>
                            </FormItem>
                            <FormItem label="Warehouse Name" labelCol={{ span: 3 }} wrapperCol={{ span: 4 }} field="warehouseId" required rules={[{ required: true }]}>
                                <WarehouseSelect addLink={true} value={warehouseId} onChange={(val) => {
                                    setWarehouseId(val);
                                }} />
                            </FormItem>
                            <FormItem label="Memo" labelCol={{ span: 3 }} field="memo">
                                <TextArea />
                            </FormItem>
                        </Col>
                    </Row>
                </FormItem>
                <FormItem label="Items" labelCol={{ span: 3 }} labelAlign="Left">
                    <Form.List field="items">
                        {(fields, { add, remove }) =>
                            <>
                                <table width="100%">
                                    <thead>
                                        <tr>
                                            <th width="120">Item Number</th>
                                            <th>Item Name</th>
                                            <th>Description</th>
                                            <th>U/M</th>
                                            <th>Qty</th>
                                            <th>Rate</th>
                                            <th>Amount</th>
                                            <th>Location</th>
                                            <th>Lot#</th>
                                            <th>Expiration Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {fields.map((item, index) => {
                                            return (
                                                <tr key={index}>
                                                    <td>
                                                        <FormItem field={item.field + ".id"} hidden>
                                                            <Input />
                                                        </FormItem>
                                                        <FormItem field={item.field + ".itemNo"} noStyle>
                                                            <Input readOnly />
                                                        </FormItem>
                                                    </td>
                                                    <td>
                                                        <FormItem field={item.field + ".itemId"} required rules={[{ required: true }]} noStyle>
                                                            <Select options={_.map(items, (item) => ({ value: item.itemId, label: item.itemName }))} style={{ minWidth: 120 }}
                                                                onChange={async (val) => {
                                                                    const item = _.find(items, item => item.itemId === val);
                                                                    form.setFieldValue("items[" + index + "].itemNo", item.itemNo);
                                                                    form.setFieldValue("items[" + index + "].itemName", item.itemName);
                                                                    form.setFieldValue("items[" + index + "].description", item.description);
                                                                    form.setFieldValue("items[" + index + "].uom", item.uom);
                                                                    form.setFieldValue("items[" + index + "].rate", item.rate);
                                                                }} />
                                                        </FormItem>
                                                        <FormItem field={item.field + ".itemName"} hidden>
                                                            <Input />
                                                        </FormItem>
                                                    </td>
                                                    <td>
                                                        <FormItem field={item.field + ".description"} noStyle>
                                                            <Input readOnly />
                                                        </FormItem>
                                                    </td>
                                                    <td>
                                                        <FormItem field={item.field + ".uom"} noStyle>
                                                            <Input readOnly />
                                                        </FormItem>
                                                    </td>
                                                    <td>
                                                        <FormItem field={item.field + ".quantity"} noStyle>
                                                            <InputNumber onBlur={() => calcAmount(index)} />
                                                        </FormItem>
                                                    </td>
                                                    <td>
                                                        <FormItem field={item.field + ".rate"} noStyle>
                                                            <InputNumber onBlur={() => calcAmount(index)} />
                                                        </FormItem>
                                                    </td>
                                                    <td>
                                                        <FormItem field={item.field + ".amount"} noStyle>
                                                            <InputNumber />
                                                        </FormItem>
                                                    </td>
                                                    <td>
                                                        <FormItem field={item.field + ".locationId"} noStyle>
                                                            <LocationSelect onlyActive={false} warehouseId={warehouseId} style={{ width: 110 }} />
                                                        </FormItem>
                                                    </td>
                                                    <td>
                                                        <FormItem field={item.field + ".lot"} noStyle>
                                                            <Input />
                                                        </FormItem>
                                                    </td>
                                                    <td>
                                                        <FormItem field={item.field + ".expiredDate"} noStyle>
                                                            <DatePicker />
                                                        </FormItem>
                                                    </td>
                                                    <td>
                                                        <FormItem noStyle>
                                                            <Button type="text" status="danger" icon={<IconDelete />}
                                                                onClick={() => remove(index)}></Button>
                                                        </FormItem>
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                                <FormItem>
                                    <Button type="text" icon={<IconPlus />} onClick={() => add()}>Add Item</Button>
                                </FormItem>
                            </>
                        }
                    </Form.List>
                </FormItem>
            </Form>
        </Card>
    )
}
